import { computed } from 'mobx';
import $labels from 'app/stores/label/$labels';
import $notifications from 'app/stores/notifications/$notifications';
import $runbook from 'app/stores/runbook/$runbook';
import { getPolicyManageUrl } from 'app/stores/alerting/policyUtils';
import { POLICY_APPLICATION_LABELS } from '@kentik/ui-shared/alerting/constants';
import { getTopLevelApplication } from '@kentik/ui-shared/alerting/applicationUtils';
import Model from 'core/model/Model';

export default class BasePolicyModel extends Model {
  get urlRoot() {
    return '/api/ui/alerting/policies';
  }

  get sortValues() {
    return {
      name: () => this.name.trim().toLowerCase(),
      runbooks: () => $runbook.getRunbooksForPolicy(this).length
    };
  }

  // Used as an alias for server-side filtering purposes
  get policyID() {
    return this.id;
  }

  get name() {
    return this.get('name');
  }

  get policyName() {
    return this.get('name');
  }

  get description() {
    return this.get('description');
  }

  get ruleId() {
    throw new Error('BasePolicyModel: ruleId is not implemented.');
  }

  get lastModified() {
    throw new Error('BasePolicyModel: lastModified is not implemented.');
  }

  get application() {
    return this.get('application');
  }

  get topLevelApplication() {
    return getTopLevelApplication(this.application);
  }

  get devices() {
    return this.get('device_selector') || {};
  }

  get dimensions() {
    return [];
  }

  get metrics() {
    return [];
  }

  get userId() {
    return this.get('user_id');
  }

  @computed
  get manageUrl() {
    return getPolicyManageUrl(this.application, !this.isNew && this.id);
  }

  @computed
  get cloneUrl() {
    return getPolicyManageUrl(this.application, false);
  }

  // Policy type values & checks

  @computed
  get applicationLabel() {
    return POLICY_APPLICATION_LABELS[this.topLevelApplication];
  }

  @computed
  get labels() {
    return this.id ? $labels.getLabels('policy', this.id) : [];
  }

  /**
   * Returns form-ready values for this policy model.
   * By default returns raw attributes; subclasses (e.g. EventPolicyModel) may override
   * to perform additional transformations before the values are used in the unified form.
   */
  getFormValues() {
    return this.attributes;
  }

  // Model serialization

  get omitDuringSerialize() {
    return ['uiProperties'];
  }

  serialize(...args) {
    return super.serialize(...args);
  }

  // Model actions

  fetch(...args) {
    // fetch notification channels first to prevent a race condition where the notification channels
    // are not available during deserialization of the policy collection
    return $notifications.collection.fetch().then(() => super.fetch(...args));
  }

  save(...args) {
    return super.save(...args);
  }
}
